/**
 * FunctionalHarmonyGraph.js
 * Defines the transition probabilities between scale degrees (Functions)
 * for Major and Minor keys.
 *
 * Nodes: Scale Degrees (1-7) represented as integers (0-6 index).
 * Edges: Transition weights (0.0 - 1.0).
 */

export const HARMONIC_GRAPH = {
    major: {
        // I (Tonic) - Can go anywhere, but prefers functional movement
        0: [
            { target: 3, weight: 0.3 }, // I -> IV (Subdominant)
            { target: 4, weight: 0.3 }, // I -> V (Dominant)
            { target: 5, weight: 0.2 }, // I -> vi (Submediant)
            { target: 1, weight: 0.1 }, // I -> ii (Supertonic)
            { target: 2, weight: 0.05 }, // I -> iii (Mediant)
            { target: 0, weight: 0.05 }  // I -> I (Static)
        ],
        // ii (Supertonic / Predominant)
        1: [
            { target: 4, weight: 0.6 }, // ii -> V (Standard Jazz/Pop)
            { target: 6, weight: 0.1 }, // ii -> vii°
            { target: 3, weight: 0.2 }, // ii -> IV (Plagal slide)
            { target: 0, weight: 0.1 }  // ii -> I (Soft resolution)
        ],
        // iii (Mediant)
        2: [
            { target: 5, weight: 0.5 }, // iii -> vi (Circle of 5ths)
            { target: 3, weight: 0.3 }, // iii -> IV
            { target: 0, weight: 0.2 }  // iii -> I
        ],
        // IV (Subdominant / Predominant)
        3: [
            { target: 4, weight: 0.4 }, // IV -> V (Half Cadence approach)
            { target: 0, weight: 0.3 }, // IV -> I (Plagal)
            { target: 1, weight: 0.2 }, // IV -> ii
            { target: 5, weight: 0.1 }  // IV -> vi (Deceptive-ish)
        ],
        // V (Dominant)
        4: [
            { target: 0, weight: 0.6 }, // V -> I (Perfect Cadence)
            { target: 5, weight: 0.2 }, // V -> vi (Deceptive Cadence)
            { target: 3, weight: 0.1 }, // V -> IV (Blues/Pop retrogression)
            { target: 2, weight: 0.1 }  // V -> iii
        ],
        // vi (Submediant / Relative Minor)
        5: [
            { target: 3, weight: 0.3 }, // vi -> IV
            { target: 1, weight: 0.3 }, // vi -> ii
            { target: 4, weight: 0.2 }, // vi -> V
            { target: 2, weight: 0.2 }  // vi -> iii
        ],
        // vii° (Leading Tone)
        6: [
            { target: 0, weight: 0.8 }, // vii° -> I
            { target: 5, weight: 0.2 }  // vii° -> vi
        ]
    },
    minor: {
        // i (Tonic)
        0: [
            { target: 3, weight: 0.2 }, // i -> iv
            { target: 4, weight: 0.2 }, // i -> v/V
            { target: 5, weight: 0.3 }, // i -> VI
            { target: 6, weight: 0.2 }, // i -> VII (Natural Minor)
            { target: 2, weight: 0.1 }  // i -> III (Relative Major)
        ],
        // ii° (Supertonic)
        1: [
            { target: 4, weight: 0.7 }, // ii° -> V
            { target: 6, weight: 0.2 }, // ii° -> vii°
            { target: 3, weight: 0.1 }  // ii° -> iv
        ],
        // III (Mediant)
        2: [
            { target: 5, weight: 0.4 }, // III -> VI
            { target: 6, weight: 0.4 }, // III -> VII
            { target: 3, weight: 0.2 }  // III -> iv
        ],
        // iv (Subdominant)
        3: [
            { target: 4, weight: 0.5 }, // iv -> V
            { target: 0, weight: 0.3 }, // iv -> i
            { target: 6, weight: 0.2 }  // iv -> VII
        ],
        // v / V (Dominant) - We assume harmonic minor often for V
        4: [
            { target: 0, weight: 0.7 }, // V -> i
            { target: 5, weight: 0.2 }, // V -> VI (Deceptive)
            { target: 6, weight: 0.1 }  // V -> VII (Backdoor)
        ],
        // VI (Submediant)
        5: [
            { target: 3, weight: 0.3 }, // VI -> iv
            { target: 1, weight: 0.2 }, // VI -> ii°
            { target: 4, weight: 0.2 }, // VI -> V
            { target: 6, weight: 0.3 }  // VI -> VII
        ],
        // VII (Subtonic / Leading Tone)
        6: [
            { target: 2, weight: 0.5 }, // VII -> III (Resolution to Relative Major)
            { target: 0, weight: 0.3 }, // VII -> i
            { target: 4, weight: 0.2 }  // VII -> V
        ]
    }
};

/**
 * Helper: Resolve Quality for Graph Node
 * @param {number} degree - 0-6
 * @param {string} scaleType - 'major' or 'minor'
 * @returns {string} quality ('major', 'minor', 'diminished', 'augmented')
 */
export const getGraphChordQuality = (degree, scaleType) => {
    if (scaleType === 'major') {
        if ([0, 3, 4].includes(degree)) return 'major';
        if ([1, 2, 5].includes(degree)) return 'minor';
        if (degree === 6) return 'diminished';
    } else {
        // Minor (Natural/Harmonic Hybrid)
        if ([0, 3].includes(degree)) return 'minor'; // i, iv
        if ([2, 5, 6].includes(degree)) return 'major'; // III, VI, VII
        if (degree === 1) return 'diminished'; // ii°
        if (degree === 4) return 'minor'; // v (default, often mutated to V in generation)
    }
    return 'major';
};
